'use client';

import Image from 'next/image';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@/primitives';

export interface EbvPotentialChangeInfoDialogProps {
  open: boolean;
  onClose: () => void;
}

/** "About the EBV Potential Change Graph" info dialog. */
export default function EbvPotentialChangeInfoDialog({
  open,
  onClose,
}: EbvPotentialChangeInfoDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="ebv-potential-change-graph-info-title"
    >
      <DialogTitle
        id="ebv-potential-change-graph-info-title"
        component="div"
        variant="headlinesmall"
      >
        About the EBV Potential Change Graph
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        <Stack
          spacing={2}
          sx={{
            mb: 3,
            '& strong': { fontWeight: 700 },
          }}
        >
          <Typography
            variant="bodymedium"
            component="p"
            sx={{ color: 'text.primary', m: 0 }}
          >
            The <strong>Possible Change Graph</strong>
            {` `}indicates how an animal&apos;s EBVs could possibly change in
            the future as additional information (such as own performance,
            progeny performance and/or genomic data) becomes available. Each bar
            represents{' '}
            <strong>
              one standard error on either side of the current EBV.
            </strong>
          </Typography>
          <Typography
            variant="bodymedium"
            component="p"
            sx={{ color: 'text.primary', m: 0 }}
          >
            The length of the bar reflects the{' '}
            <strong>degree of uncertainty</strong> associated with the EBV.{' '}
            <strong>Longer bars</strong> indicate lower EBV accuracy and a
            greater likelihood of EBV movement (which, depending on movement
            direction, could be favourable or unfavourable).{' '}
            <strong>Shorter bars</strong> indicate higher accuracy and more
            stable, well-proven EBVs. EBV accuracy percentages, shown on the
            graph, help quantify this confidence.
          </Typography>
          <Typography
            variant="bodymedium"
            component="p"
            sx={{ color: 'text.primary', m: 0 }}
          >
            Animals with <strong>lower accuracy EBVs</strong> (such as
            two-year-old sale bulls) carry higher risk but may offer greater
            genetic opportunity for breeders comfortable with change. Animals
            with <strong>higher accuracy EBVs</strong> (such as AI sires) suit
            breeders seeking predictability and lower risk. The risk associated
            with lower accuracy animals can be{' '}
            <strong>mitigated through a bull team approach</strong>, spreading
            genetic risk across multiple sires rather than relying on a single
            animal.
          </Typography>
          <Typography
            variant="bodymedium"
            component="p"
            sx={{ color: 'text.primary', m: 0 }}
          >
            Overall, the graph supports selection decisions that align with{' '}
            <strong>
              breeding objectives and individual attitudes to risk.
            </strong>
          </Typography>
        </Stack>

        <Stack spacing={3}>
          <Box sx={{ width: '100%' }}>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                borderRadius: 2,
                overflow: 'hidden',
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Image
                src="/images/ebv-potential-change-low-accuracy-young-animal-example.png"
                alt="EBV Potential Change Graph example for a lower-accuracy animal: wider bars and lower ACC percentages"
                width={991}
                height={590}
                sizes="(max-width: 900px) 100vw, 860px"
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
            </Box>
            <Typography
              variant="bodymedium"
              component="p"
              sx={{
                textAlign: 'center',
                color: 'text.secondary',
                mt: 1.5,
                m: 0,
              }}
            >
              Example of an animal with lower EBV accuracy - the longer bars
              indicate a greater likelihood of EBV movement
            </Typography>
          </Box>

          <Box sx={{ width: '100%' }}>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                borderRadius: 2,
                overflow: 'hidden',
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Image
                src="/images/ebv-potential-change-high-accuracy-sire-example.png"
                alt="EBV Potential Change Graph example for a higher-accuracy animal: shorter bars and high ACC percentages"
                width={990}
                height={590}
                sizes="(max-width: 900px) 100vw, 860px"
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
            </Box>
            <Typography
              variant="bodymedium"
              component="p"
              sx={{
                textAlign: 'center',
                color: 'text.secondary',
                mt: 1.5,
                m: 0,
              }}
            >
              Example of an animal with higher EBV accuracy - the shorter bars
              indicate less likelihood of EBV movement and more stable,
              well-proven EBVs
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
